import React, { forwardRef } from "react";
import { toMoney } from "../../utils/toMoney";
import { getCleanedInvoiceNumber } from "../../utils/getCleanedInvoiceNumber";
import { useTranslation } from "react-i18next";
import useLanguage from "../../utils/useLanguage";

const InvoicePrintComponent = forwardRef(
  ({ invoiceData, supplierData }, ref) => {
    const { t } = useTranslation("general");

    const { currentDirection } = useLanguage();

    const formatDate = (date) => {
      if (!date) return "Invalid Date";
      return new Date(date).toISOString().split("T")[0];
    };

    return (
      <div ref={ref} className="indi-print-container" dir={currentDirection}>
        {/* Header */}
        <div className="indi-print-header">
          <h1 className="indi-title">{t("invoiceDetails")}</h1>
        </div>

        {/* User Info */}
        <div className="o-column">
          <div className="indi-user-info">
            <p>
              <strong>{t("user")}:</strong> {localStorage.getItem("username")}
            </p>
            <p>
              <strong>{t("printDate")}:</strong>{" "}
              {new Date().toLocaleDateString()}
            </p>
          </div>

          {/* Main Invoice Grid */}
          <div className="indi-invoice-grid">
            {/* Left Column */}
            <div>
              <div className="indi-section">
                <h3 className="indi-section-title">{t("invoiceInfo")}</h3>

                <div className="indi-field">
                  <span className="indi-field-label">
                    {t("invoiceNumber")}:
                  </span>
                  <span className="indi-field-value">
                    {getCleanedInvoiceNumber(invoiceData?.invoiceNumber)}
                  </span>
                </div>

                <div className="indi-field">
                  <span className="indi-field-label">{t("invoiceDate")}:</span>
                  <span className="indi-field-value">
                    {formatDate(invoiceData?.date)}
                  </span>
                </div>

                <div className="indi-field">
                  <span className="indi-field-label">{t("supplier")}:</span>
                  <span className="indi-field-value">{supplierData?.name}</span>
                </div>
              </div>

              <div className="indi-section">
                <h3 className="indi-section-title">{t("bankDetails")}</h3>

                <div className="indi-field">
                  <span className="indi-field-label">{t("bankName")}:</span>
                  <span className="indi-field-value">
                    {invoiceData?.bankName || (
                      <span className="indi-no-data">N/A</span>
                    )}
                  </span>
                </div>

                <div className="indi-field">
                  <span className="indi-field-label">{t("SWIFT")}:</span>
                  <span className="indi-field-value">
                    {invoiceData?.swift || (
                      <span className="indi-no-data">N/A</span>
                    )}
                  </span>
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div>
              <div className="indi-section">
                <h3 className="indi-section-title">{t("amountDetails")}</h3>

                <div className="indi-field">
                  <span className="indi-field-label">{t("amount")}:</span>
                  <span className="indi-field-value">
                    {toMoney(invoiceData?.amount)} {invoiceData?.amountCurrency}
                  </span>
                </div>

                <div className="indi-field">
                  <span className="indi-field-label">{t("currency")}:</span>
                  <span className="indi-field-value">
                    {invoiceData?.amountCurrency}
                  </span>
                </div>

                <div className="indi-field">
                  <span className="indi-field-label">
                    {t("amountInSupplierCurrency")}:
                  </span>
                  <span className="indi-field-value">
                    {toMoney(invoiceData?.amountInSupCur)}{" "}
                    {supplierData?.currency}
                  </span>
                </div>
              </div>

              <div className="indi-section">
                <h3 className="indi-section-title">{t("balanceInfo")}</h3>

                <div className="indi-field">
                  <span className="indi-field-label">
                    {t("balanceBeforeUpdate")}:
                  </span>
                  <span className="indi-field-value">
                    {toMoney(invoiceData?.balanceBeforeUpdate)}{" "}
                    {supplierData?.currency}
                  </span>
                </div>

                <div className="indi-field">
                  <span className="indi-field-label">
                    {t("balanceAfterUpdate")}:
                  </span>
                  <span className="indi-field-value">
                    {toMoney(invoiceData?.balanceAfterUpdate)}{" "}
                    {supplierData?.currency}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Amount Summary */}
          <div className="indi-amount-section">
            <div className="indi-amount-row">
              <span>{t("invoiceAmount")}:</span>
              <span>
                {toMoney(invoiceData?.amount)} {invoiceData?.amountCurrency}
              </span>
            </div>
            <div className="indi-amount-row">
              <span>{t("invoiceAmountInSupplierCurrency")}:</span>
              <span>
                {toMoney(invoiceData?.amountInSupCur)} {supplierData?.currency}
              </span>
            </div>
            <div className="indi-amount-row indi-main-amount">
              <span>{t("totalAmount")}:</span>
              <span>
                {toMoney(invoiceData?.amountInSupCur)} {supplierData?.currency}
              </span>
            </div>
          </div>

          {/* Notes Section */}
          {invoiceData?.notes && (
            <div className="indi-notes-section">
              <h3 className="indi-notes-title">{t("notes")}:</h3>
              <p className="indi-notes-text">{invoiceData?.notes}</p>
            </div>
          )}
        </div>

        <div className="indi-footer t-text-mini">
          <p className="t-headline-3">{t("ehsiblySecondSlogan")}</p>
          <p className="indi-alhabbar-slogan">{t("alhabbarSlogan")}</p>
        </div>
      </div>
    );
  }
);

InvoicePrintComponent.displayName = "InvoicePrintComponent";

export default InvoicePrintComponent;
